import TagManager from 'react-gtm-module'

const GTM_CONTAINER_ID = 'GTM-WNJPTPFP'

type Nullable<T> = T | null | undefined

interface ClickEventPayload {
  module: string
  module_title: string
  click_text: string
  click_url: string
  click_element: string
}

// const accentRemovalRegex = /[\u00C0-\u017F]/g;

function normalizeValue(value: Nullable<string>): string | null {
  if (value === null || value === undefined) return null
  const accentRemovalRegex = /[\u00C0-\u017F]/g
  const trimmed = value.trim()
  if (!trimmed) return null

  // Remove diacritics + lowercase
  const decomposed = trimmed.normalize('NFD')
  const withoutAccents = decomposed.replace(accentRemovalRegex, m => m)
  // Remove remaining combining diacritic marks (safe in older TS targets)
  const stripped = withoutAccents.replace(/[\u0300-\u036f]/g, '')

  return stripped.toLowerCase()
}

function normalizePayload(payload: ClickEventPayload): ClickEventPayload | null {
  const module = normalizeValue(payload.module)
  const module_title = normalizeValue(payload.module_title)
  const click_text = normalizeValue(payload.click_text)
  const click_url = normalizeValue(payload.click_url)
  const click_element = normalizeValue(payload.click_element)

  if (!module || !module_title || !click_text || !click_url || !click_element) return null

  return {
    module,
    module_title,
    click_text,
    click_url,
    click_element,
  }
}

let initialized = false

export function initializeGTM(): void {
  if (initialized) return
  initialized = true

  TagManager.initialize({
    gtmId: GTM_CONTAINER_ID,
    dataLayerName: 'dataLayer',
  })
}

export function pushToDataLayer(eventObj: Record<string, unknown>): void {
  if (typeof window === 'undefined') return

  const w = window as any
  if (!w.dataLayer) w.dataLayer = []

  w.dataLayer.push(eventObj)
}

export function trackPageView(pagePath: Nullable<string>): void {
  const normalized = normalizeValue(pagePath)
  if (!normalized) return

  pushToDataLayer({
    event: 'page_view',
    page_path: normalized,
  })
}

export function trackClickEvent(payload: Nullable<ClickEventPayload>): void {
  if (!payload) return
  const normalized = normalizePayload(payload)
  if (!normalized) return

  pushToDataLayer({
    event: 'click',
    ...normalized,
  })
}

export function normalizeAnalyticsParams(params: Record<string, Nullable<string>>): Record<string, string> {
  const out: Record<string, string> = {}

  for (const [key, value] of Object.entries(params)) {
    const normalized = normalizeValue(value)
    if (!normalized) continue
    out[key.toLowerCase()] = normalized
  }

  return out
}
